import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CssBaseline,
  Container,
  Dialog,
  Chip,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Card,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Delete, ArrowBack, Search as SearchIcon } from '@mui/icons-material';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import AppToast from '../../components/Toast/AppToast';

const LIMIT = 10;

const ExerciseListPage = () => {
  const { colors: appColors } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [openAssignId, setOpenAssignId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignFile, setAssignFile] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [members, setMembers] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const getToken = () => JSON.parse(localStorage.getItem('userdata') || '{}')?.token;

  const fetchExercises = async (reset = true) => {
    const token = getToken();
    if (!token) return;
    const nextIndex = reset ? 0 : index;
    if (reset) {
      setLoading(true);
      setExercises([]);
    } else setLoadingMore(true);
    try {
      const res = await fetch(`${window.constants.api_url}/exercises`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          limit: LIMIT,
          index: nextIndex,
          search_text: searchText || undefined,
        }),
      });
      const data = await res.json();
      const list = Array.isArray(data.data) ? data.data : [];
      if (reset) {
        setExercises(list);
        setIndex(LIMIT);
      } else {
        setExercises((prev) => [...prev, ...list]);
        setIndex((i) => i + LIMIT);
      }
      const total = data.total ?? list.length;
      setHasMore(list.length >= LIMIT && (data.total == null || nextIndex + list.length < total));
    } catch (e) {
      console.error('Error fetching exercises:', e);
      if (reset) setExercises([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchMembers = async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${window.constants.api_url}/members`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.code === 200) setMembers(data.data || []);
    } catch (e) {
      console.error('Error fetching members:', e);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    fetchExercises(true);
  }, [searchText]);

  const handleSearch = () => setSearchText(searchInput.trim());
  const handleDelete = async () => {
    if (!deleteId) return;
    const token = getToken();
    if (!token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${window.constants.api_url}/exercise/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id: deleteId }),
      });
      const data = await res.json();
      if (data.code === 200) {
        fetchExercises(true);
      } else {
        console.error('Delete failed:', data?.message);
      }
    } catch (e) {
      console.error('Error deleting exercise:', e);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleAssign = async () => {
    if (!openAssignId || !selectedUsers?.length) return;
    const token = getToken();
    if (!token) return;
    try {
      let res;
      if (assignFile) {
        const fd = new FormData();
        fd.append('exercise_id', openAssignId);
        selectedUsers.forEach((id) => fd.append('member_id[]', id));
        fd.append('attachment', assignFile);
        res = await fetch(`${window.constants.api_url}/assign-member-to-exercise`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } else {
        res = await fetch(`${window.constants.api_url}/assign-member-to-exercise`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ exercise_id: openAssignId, member_id: selectedUsers }),
        });
      }
      const data = await res.json();
      if (data.code === 200) {
        setSelectedUsers([]);
        setAssignFile(null);
        setOpenAssignId(null);
        setToast({ open: true, message: data.message || 'Members assigned successfully.', severity: 'success' });
        navigate('/exercise-assign-user');
      } else {
        setToast({ open: true, message: data?.message || 'Assignment failed.', severity: 'error' });
      }
    } catch (e) {
      console.error('Error assigning:', e);
      setToast({ open: true, message: 'An error occurred. Please try again.', severity: 'error' });
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, mt: 8 }}>
        <Container maxWidth="xl">
          {/* Compact header: sub-nav + title + search + action in one bar */}
          <Card
            sx={{
              mb: 2,
              borderRadius: 1.5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              spacing={1.5}
              sx={{ px: 1.5, py: 1 }}
            >
              <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={0}>
                <IconButton
                  size="small"
                  onClick={() => navigate(-1)}
                  sx={{ color: appColors.evergreen, '&:hover': { bgcolor: 'rgba(31, 122, 84, 0.08)' } }}
                  aria-label="Back"
                >
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="subtitle1" sx={{ color: appColors.evergreen, fontWeight: 600 }}>
                  Exercises
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
                  ·
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate('/exercise-list')}
                  sx={{ minWidth: 0, px: 0.5, color: appColors.evergreen, textTransform: 'none', fontWeight: 500 }}
                >
                  Exercise List
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate('/exercise-assign-user')}
                  sx={{ minWidth: 0, px: 0.5, color: 'text.secondary', textTransform: 'none', '&:hover': { color: appColors.evergreen } }}
                >
                  Assign User
                </Button>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ '& .MuiSvgIcon-root': { fontSize: 18 } }}>
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { backgroundColor: 'action.hover', '& fieldset': { borderColor: 'transparent' }, borderRadius: 1 },
                  }}
                  sx={{ width: { xs: 120, sm: 160 } }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSearch}
                  sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none', minWidth: 0 }}
                >
                  Search
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate('/create-exercise')}
                  sx={{ bgcolor: appColors.evergreen, '&:hover': { bgcolor: appColors.pine }, textTransform: 'none' }}
                >
                  Create
                </Button>
              </Stack>
            </Stack>
          </Card>

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: appColors.evergreen }} /></Box>
          ) : (
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: appColors.evergreen }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>#</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Exercise</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                      {!isMobile && (
                        <>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Sets</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Reps</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Duration</TableCell>
                        </>
                      )}
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exercises.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No exercises found.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      exercises.map((row, i) => (
                        <TableRow
                          key={row.id}
                          sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8fafc' }, '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{row.exercise_name ?? '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography noWrap variant="body2">{row.description ?? '-'}</Typography>
                          </TableCell>
                          {!isMobile && (
                            <>
                              <TableCell>{row.sets ?? '-'}</TableCell>
                              <TableCell>{row.reps ?? '-'}</TableCell>
                              <TableCell>{row.duration ?? '-'}</TableCell>
                            </>
                          )}
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <IconButton
                                size="small"
                                onClick={() => { setOpenAssignId(row.id); setSelectedUsers([]); }}
                                sx={{ color: '#047857', bgcolor: 'rgba(4, 120, 87, 0.05)', '&:hover': { bgcolor: '#047857', color: 'white' } }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteId(row.id)}
                                sx={{ color: '#dc2626', bgcolor: 'rgba(220, 38, 38, 0.05)', '&:hover': { bgcolor: '#dc2626', color: 'white' } }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {hasMore && exercises.length > 0 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="outlined" onClick={() => fetchExercises(false)} disabled={loadingMore} sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>
                    {loadingMore ? <CircularProgress size={24} /> : 'Load more'}
                  </Button>
                </Box>
              )}
            </Card>
          )}

          {/* Assign Dialog */}
          <Dialog open={Boolean(openAssignId)} onClose={() => { setOpenAssignId(null); setSelectedUsers([]); setAssignFile(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ bgcolor: appColors.evergreen, color: 'white', py: 2 }}>Assign Members</DialogTitle>
            <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Autocomplete
                multiple
                options={members}
                getOptionLabel={(m) => m.membership_info ?? ''}
                value={members.filter((m) => selectedUsers.includes(m.id))}
                onChange={(_, newVal) => setSelectedUsers(newVal.map((m) => m.id))}
                filterSelectedOptions
                renderTags={(selected, getTagProps) =>
                  selected.map((m, index) => (
                    <Chip key={m.id} label={m.membership_info} size="small"
                      sx={{ bgcolor: 'rgba(27,67,50,0.1)', color: appColors.evergreen, fontWeight: 600 }}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Search & Select Members" placeholder="Type name or membership no..."
                    sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: appColors.evergreen } }, '& label.Mui-focused': { color: appColors.evergreen } }}
                  />
                )}
                ListboxProps={{ style: { maxHeight: 260 } }}
              />

              {/* File Attachment */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Attachment (PDF or Excel) — optional
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'none', color: appColors.evergreen, borderColor: appColors.evergreen, '&:hover': { borderColor: appColors.pine, bgcolor: 'rgba(27,67,50,0.04)' } }}
                >
                  📎 {assignFile ? 'Change file' : 'Upload file'}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.xls,.xlsx,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setAssignFile(e.target.files[0] || null)}
                  />
                </Button>
                {assignFile && (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={assignFile.name}
                      size="small"
                      onDelete={() => setAssignFile(null)}
                      sx={{ bgcolor: appColors.leafSoft, color: appColors.evergreen, maxWidth: 320 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {(assignFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => { setOpenAssignId(null); setSelectedUsers([]); setAssignFile(null); }} variant="outlined" sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>Cancel</Button>
              <Button onClick={handleAssign} variant="contained" sx={{ bgcolor: appColors.evergreen, '&:hover': { bgcolor: appColors.pine }, textTransform: 'none' }}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Delete Dialog */}
          <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ bgcolor: appColors.evergreen, color: 'white', py: 2 }}>Delete Exercise</DialogTitle>
            <DialogContent sx={{ mt: 2 }}><Typography>Are you sure you want to delete this exercise?</Typography></DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setDeleteId(null)} variant="outlined" disabled={deleting} sx={{ color: appColors.evergreen, borderColor: appColors.evergreen, textTransform: 'none' }}>Cancel</Button>
              <Button onClick={handleDelete} variant="contained" disabled={deleting} sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>
                {deleting ? <CircularProgress size={24} /> : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
      <AppToast open={toast.open} message={toast.message} severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </Box>
  );
};

export default ExerciseListPage;
